import {memo, useCallback} from 'react';

import styled from 'styled-components';
import moment from 'moment-timezone';
import numeral from 'numeral';

import {getLocalizationService} from '~/shared/services/localisationService';
import {ActionButton} from '~/shared/components/Button';
import {createLogger} from '~/shared/logging';
import {UserVoucher} from '~/shared/store/models/User/UserVouchers';
import {FlexCenter} from '~/shared/theme/FlexLayout';
import {getIsFreeFitVendor, getVendorDisclaimer} from '~/shared/utils/barcode';
import {CopyToClipboardButton} from '~/shared/components/CopyToClipboardButton';

import {User} from '../../store/models';
import {body14Normal, body16Bold, body18Bold} from '../../theme/typography';
import {EMPTY_OBJECT} from '../../consts/commonConsts';
import printCoupon from '../../utils/printCoupon';
import {NumericFormats} from '../../consts/checkoutConsts';

const logger = createLogger('CouponBarCodeComponent');

const BoldInfoText = styled.div`
  ${body16Bold};
`;

const GeneralInfo = styled.div<{remarksNotExist?: boolean}>`
  background: ${({theme}) => theme.colors.surface};
  padding-right: 18px;
  padding-left: 18px;
  color: ${({theme}) => theme.orderSuccess.body.headerTextColor};
  padding-bottom: ${({remarksNotExist}) => remarksNotExist && '16px'};
`;

const CouponRemarks = styled.div`
  background: ${({theme}) => theme.colors.surface};
  padding-right: 18px;
  padding-left: 18px;
  color: ${({theme}) => theme.orderSuccess.body.headerTextColor};
  font-weight: bold;
  padding-bottom: 16px;
`;

const BarCodeSection = styled.div`
  padding-top: 32px;
  background: ${({theme}) => theme.colors.surface};
  margin-top: 8px;
`;

const BarCodeSubheaderSection = styled.div`
  ${body18Bold};
  color: ${({theme}) => theme.orderSuccess.body.headerTextColor};
  background: ${({theme}) => theme.colors.surface};
  padding-right: 18px;
  padding-left: 18px;
`;

const ButtonsContainer = styled.div`
  background: ${({theme}) => theme.colors.surface};
  text-align: center;
  padding-top: 10px;
  padding-bottom: 16px;
  width: 100%;
`;

const BarCodeNumber = styled.div`
  margin: auto;
  text-align: center;
  ${body14Normal};
  padding-top: 13px;
  color: ${({theme}) => theme.colors.text};
  background: ${({theme}) => theme.colors.surface};
`;

const ExplanationText = styled.div<{isFreefit: boolean}>`
  ${({isFreefit}) => !isFreefit && 'text-align: center;'}
  padding: ${({isFreefit}) => (isFreefit ? '18px 18px 40px' : '18px 10px 10px')};
  color: ${({theme}) => theme.colors.secondary};
  background: ${({theme}) => theme.colors.surface};
`;

const BarCodeImg = styled.div.attrs(({imageUrl}: {imageUrl?: string}) => ({
  style: {backgroundImage: imageUrl && `url(${imageUrl})`},
}))<{imageUrl?: string}>`
  background-position: center;
  height: 57px;
  margin: auto;
  background-size: contain;
  background-repeat: no-repeat;
  width: 180px;
`;

const PrintButton = styled(ActionButton)`
  border-radius: 0;
  box-shadow: none;
  padding-top: 0;
  width: 252px;
`;

const getTranslation = ({used, dateUsed, usedInBranchName: branchName, validDate}: UserVoucher) => {
  const {t} = getLocalizationService();

  const date = dateUsed ? moment(dateUsed).format('DD.MM.YYYY') : null;
  let translated = t('order_success_coupon_remarks', {validDate});

  if (used) {
    translated = t('the_voucher_was_used');
    if (date && branchName) {
      translated = t('the_voucher_was_used', {context: 'both', date, branchName});
    } else if (date) {
      translated = t('the_voucher_was_used', {context: 'date', date});
    } else if (branchName) {
      translated = t('the_voucher_was_used', {context: 'branchName', branchName});
    }
  }
  return translated;
};

export interface ICouponBarCodeComponent {
  restaurantName: string;
  minLargeTablet: boolean;
  userData?: User;
  barCode?: UserVoucher;
}

const CouponBarCodeComponent = ({restaurantName, barCode, userData, minLargeTablet}: ICouponBarCodeComponent) => {
  const {currentLanguageDirection: direction, t} = getLocalizationService();
  const isLTR = direction === 'ltr';

  const {isGovCompany, firstName, lastName} = userData || EMPTY_OBJECT;
  const barcodeSum = numeral(barCode?.amount).format(NumericFormats.showZeroDecimals);
  const barcodeSumText = isLTR ? `₪${barcodeSum}` : `${barcodeSum}₪`;
  const print = useCallback(
    () => printCoupon({restaurantName, barCode, direction, barcodeSumText, isGovCompany, firstName, lastName, t}),
    [restaurantName, barCode, direction, barcodeSumText, isGovCompany, firstName, lastName, t],
  );

  if (!barCode) {
    logger.error('bar code is not available');
    return null;
  }

  const isFreefit = getIsFreeFitVendor(barCode?.vendor);

  const voucherTranslation = getTranslation(barCode);

  return (
    <>
      <BarCodeSubheaderSection id="modal-title" data-test-id="orderSuccessCouponHeader">{t('coupon_details')}</BarCodeSubheaderSection>
      <GeneralInfo remarksNotExist={isGovCompany} id="modal-content" data-test-id="orderSuccessCouponChainAndAmount">
        {t('coupon_to_xxx_chain_in_the_amount_of_xxx', {
          chainName: restaurantName,
          totalAmount: barCode.amount,
        })}
      </GeneralInfo>
      {!isGovCompany && <CouponRemarks data-test-id="orderSuccessCouponRemarks">{voucherTranslation}</CouponRemarks>}
      <GeneralInfo data-test-id="orderSuccessCouponUserName">
        {`${t('order_user_name')}:`}{' '}
        <b>
          {firstName} {lastName}
        </b>
      </GeneralInfo>
      <GeneralInfo>{t('voucher_is_personal_statement')}</GeneralInfo>

      <BarCodeSection>
        {isFreefit && !barCode.barCodeImgUrl ? (
          <FlexCenter>
            <CopyToClipboardButton textToCopy={barCode.barCodeNumber}>
              {barCode.barCodeNumber}
            </CopyToClipboardButton>
          </FlexCenter>
          ) : (
            <>
              <BarCodeImg data-test-id="orderSuccessBarcodeImage" imageUrl={barCode.barCodeImgUrl} />
              <BarCodeNumber data-test-id="orderSuccessBarcodeNumber">{barCode.barCodeNumber}</BarCodeNumber>
            </>
        )}
      </BarCodeSection>
      <ExplanationText isFreefit={isFreefit}>
        {!!barCode.barCodeImgUrl && <BoldInfoText>{t('bar_code_must_be_printed')}</BoldInfoText>}
        <div>{getVendorDisclaimer(barCode.vendor, isGovCompany)}</div>
      </ExplanationText>
      {minLargeTablet && !!barCode.barCodeImgUrl && (
        <ButtonsContainer>
          <PrintButton onClick={print}>
            <>{t('print_coupon')}</>
          </PrintButton>
        </ButtonsContainer>
      )}
    </>
  );
};

export default memo(CouponBarCodeComponent);
